import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { FaCheck } from "react-icons/fa";
import { ImCancelCircle } from "react-icons/im";
import { RiProgress5Line } from "react-icons/ri";
import {
  isTrainingInProgressStatus,
  isTrainingSucceededStatus,
} from "../../utils/utils";

const formatTagAliases = (tagAliasText) => {
  if (!tagAliasText) {
    return "";
  }

  return tagAliasText
    .replace(/[[\]']/g, "")
    .split(", ")
    .join("; ");
};

const parseLimitArray = (limitValue) => {
  if (Array.isArray(limitValue)) {
    return limitValue;
  }

  if (typeof limitValue === "string") {
    try {
      const parsed = JSON.parse(limitValue.replace(/'/g, '"'));
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return [];
    }
  }

  return [];
};

const getAlarmLimitsText = (data) => {
  if (!data?.DEP_TAG_ALIAS) {
    return "-";
  }

  const tags = data.DEP_TAG_ALIAS.replace(/[[\]']/g, "")
    .split(", ")
    .map((tag) => tag.trim())
    .filter(Boolean);

  const highLimits = parseLimitArray(
    data.HIGH_LIMIT_ARRAY ?? data.HIGH_LIMITS ?? data.HIGH_LIMIT,
  );
  const lowLimits = parseLimitArray(
    data.LOW_LIMIT_ARRAY ?? data.LOW_LIMITS ?? data.LOW_LIMIT,
  );

  if (tags.length === 0) {
    return "-";
  }

  return tags
    .map((tag, idx) => {
      const highValue = highLimits[idx] ?? "-";
      const lowValue = lowLimits[idx] ?? "-";
      return `${tag} - High Alarm : ${highValue}, Low Alarm : ${lowValue}`;
    })
    .join("\n");
};

const renderTrainingStatus = (trainingStatus) => {
  if (isTrainingSucceededStatus(trainingStatus)) {
    return (
      <div
        className="text-green-500 font-medium flex items-center gap-1"
        title="Completed"
      >
        <FaCheck />
        Completed
      </div>
    );
  }

  if (isTrainingInProgressStatus(trainingStatus)) {
    return (
      <div
        className="text-yellow-600 font-medium flex items-center gap-1"
        title="In Progress"
      >
        <RiProgress5Line />
        In Progress
      </div>
    );
  }

  return (
    <div
      className="text-red-600 flex font-medium items-center gap-1.5"
      title="Failed"
    >
      <ImCancelCircle /> Failed
    </div>
  );
};

const ActiveModelsTable = ({ data }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const handleRowClick = (row) => {
    if (expandedRow === row) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(row);
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.CUSTOM_NAME,
      sortable: true,
      grow: 1.5,
      cell: (row) => (
        <div
          title={row.CUSTOM_NAME}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "110px",
          }}
        >
          {row.CUSTOM_NAME}
        </div>
      ),
    },
    {
      name: "Assembly",
      selector: (row) => row.DRIVER,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div
          title={row.DRIVER}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "110px",
          }}
        >
          {row.DRIVER}
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.STATUS,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div
          title={row.STATUS}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "110px",
          }}
        >
          {row.STATUS}
        </div>
      ),
    },
    {
      name: "DGS Tags",
      selector: (row) => formatTagAliases(row.DEP_TAG_ALIAS),
      sortable: true,
      grow: 1,
      cell: (row) => {
        const tags = formatTagAliases(row.DEP_TAG_ALIAS);
        return (
          <div
            title={tags}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "210px",
            }}
          >
            {tags}
          </div>
        );
      },
    },
    {
      name: "Created Date",
      selector: (row) => row.CREATED_AT,
      sortable: true,
      width: "130px",
      cell: (row) => (
        <div
          title={row.CREATED_AT}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "120px",
          }}
        >
          {row.CREATED_AT}
        </div>
      ),
    },
    {
      name: "Start Date",
      selector: (row) => row.TRAINING_ST_DATE,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div
          title={row.TRAINING_ST_DATE}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "110px",
          }}
        >
          {row.TRAINING_ST_DATE}
        </div>
      ),
    },
    {
      name: "End Date",
      selector: (row) => row.TRAINING_END_DATE,
      sortable: true,
      cell: (row) => (
        <div
          title={row.TRAINING_END_DATE}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "110px",
          }}
        >
          {row.TRAINING_END_DATE}
        </div>
      ),
    },
    {
      name: "Training Status",
      cell: (row) => renderTrainingStatus(row.TRAINING_STATUS),
      sortable: true,
      width: "135px",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "13px",
        backgroundColor: "#e3e1e1",
      },
    },
    cells: {
      style: {
        fontSize: "13.4px",
      },
    },
    table: {
      style: {
        border: "1px solid #e3e1e1",
        margin: "0 auto",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row) => row === expandedRow,
      style: {
        borderBottom: "none",
        backgroundColor: "#ededed",
      },
    },
  ];

  let summaryText = null;
  if (data && data.CUSTOM_NAME && data.CUSTOM_DESC) {
    summaryText = (
      <p className="font-semibold text-lg text-btn-primary w-[65%] mb-5">
        {data.CUSTOM_NAME} - {data.CUSTOM_DESC}
      </p>
    );
  }

  return (
    <>
      <div className="w-[65%] ">
        <p className="font-semibold text-2xl text-btn-primary w-[65%] mb-5">
          Existing Models
        </p>
        {summaryText}
        <DataTable
          columns={columns}
          data={data}
          responsive
          customStyles={customStyles}
          expandableRows
          expandOnRowClicked
          expandableRowsHideExpander
          expandableRowExpanded={(row) => row === expandedRow}
          onRowClicked={handleRowClick}
          expandableRowsComponent={ExpandedComponent}
          conditionalRowStyles={conditionalRowStyles}
        />
      </div>
    </>
  );
};

export default ActiveModelsTable;

const ExpandedComponent = ({ data }) => {
  const alarmLimits = getAlarmLimitsText(data);

  let customName = "-";
  if (data.CUSTOM_NAME) {
    customName = data.CUSTOM_NAME;
  }

  let customDescription = "-";
  if (data.CUSTOM_DESC) {
    customDescription = data.CUSTOM_DESC;
  }

  let tagDescription = "-";
  if (data.TAG_NAME) {
    tagDescription = data.TAG_NAME.replace(/\s*\([^)]*\)/g, "")
      .replace(/[[\]']/g, " ")
      .trim();
  }

  let frequencyText = "-";
  if (data.DATA_FREQ) {
    frequencyText = `${data.DATA_FREQ} mins`;
  }

  let businessRuleSection = "-";
  if (data.BUSINESS_RULES) {
    let businessRules = [];
    try {
      businessRules = JSON.parse(data.BUSINESS_RULES.replace(/'/g, '"'));
    } catch {
      businessRules = [];
    }

    businessRuleSection = (
      <table className="mt-2 bg-white border border-gray-300 rounded">
        <thead>
          <tr className="bg-gray-100 font-normal">
            <th className="px-3 py-2 border-b text-left">Alias</th>
            <th className="px-3 py-2 border-b text-left">Operator</th>
            <th className="px-3 py-2 border-b text-left">Value</th>
          </tr>
        </thead>
        <tbody>
          {businessRules.map((rule, idx) => (
            <tr key={idx} className="border-b">
              <td className="px-3 py-2">{rule.ALIAS}</td>
              <td className="px-3 py-2">{rule.OPERATOR}</td>
              <td className="px-3 py-2">{rule.VALUE}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  let projectInfoTitle = null;
  if (Array.isArray(data.PROJECT_INFO) && data.PROJECT_INFO.length > 0) {
    projectInfoTitle = <p className="mb-1 text-lg">Project Info</p>;
  }

  let projectInfo = [];
  if (Array.isArray(data.PROJECT_INFO)) {
    projectInfo = data.PROJECT_INFO;
  }

  return (
    <div
      style={{
        padding: "10px 20px",
        fontSize: "13px",
        backgroundColor: "#f9f9f9",
        display: "flex",
        gap: "50px",
      }}
    >
      <div>
        <p className="mb-1">
          <strong>Custom Name : </strong> {customName}
        </p>
        <p className="mb-1">
          <strong>Custom Description :</strong> {customDescription}
        </p>
        <p className="mb-1 ">
          <strong>DGS Tags :</strong> {formatTagAliases(data.DEP_TAG_ALIAS)}
        </p>
        <p className="mb-1">
          <strong>Tag Description : </strong> {tagDescription}
        </p>
        <p className="mb-1">
          <strong>Alarm Limits :</strong>
          <br />
          <pre style={{ margin: 0, padding: 0, background: "none" }}>
            {alarmLimits}
          </pre>
        </p>
        <p className="mb-1">
          <strong>Model Name :</strong> {data.MODEL_ID}
        </p>
        <p className="mb-1">
          <strong>Datasource Id :</strong> {data.DATASOURCE}
        </p>
        <p className="mb-1">
          <strong>Project Name :</strong> {data.PROJECTNAME}
        </p>
        <p className="mb-1">
          <strong>Driver : </strong>
          {data.DRIVER}
        </p>
        <p className="mb-1">
          <strong>Frequency : </strong>
          {frequencyText}
        </p>

        <p className="mb-1">
          <strong>Business Rule :</strong> {businessRuleSection}
        </p>
      </div>
      <div>
        {projectInfoTitle}
        <ul>
          {projectInfo.map((info, index) => (
            <li key={index}>
              <p className="mb-1 ">
                <strong>Make Manufacturer:</strong> {info.MAKE_MANUFACTURER}
                <br />
              </p>
              <p className="mb-1">
                <strong>Seal Type:</strong> {info.SEAL_TYPE}
                <br />
              </p>
              <p className="mb-1">
                <strong>Design Pressure (Bar):</strong>{" "}
                {info.DESIGN_PRESSURE_BAR || "N/A"}
                <br />
              </p>
              <p className="mb-1">
                <strong>Direction of Rotation:</strong>{" "}
                {info.DIRECTION_OF_ROTATION}
                <br />
              </p>
              <p className="mb-1">
                <strong>Trip Speed (RPM):</strong>{" "}
                {info.TRIP_SPEED_RPM || "N/A"}
                <br />
              </p>
              <p className="mb-1">
                <strong>Machine Maximum Operating Speed (RPM):</strong>{" "}
                {info.MAXIMUM_OPERATING_SPEED_RPM}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
